import type { SigningAdapter } from '../adapters/base-adapter';
import { HyperliquidAPIClient } from './utils/api';
import { HyperliquidWebSocketClient } from './utils/websocket-api';
import { createL1ActionHash } from './utils/action-hash';
import { splitSignature } from './utils/signing';
import { usdToMicroUsd } from './utils/formatting';
import { setDebugMode, debugLog } from './utils/logger';
import {
  parseOrderResponse,
  parseCancelResponse,
  parseSuccessResponse,
  type ParsedResponse,
} from './utils/response-parser';
import { AssetMetadataManager } from './asset-manager';
import type * as types from './types';
import type { GenericAction } from '../types/actions';

/**
 * Base Hyperliquid client with all trading methods
 * Shared by both frontend and backend clients
 */
export abstract class HyperliquidBaseClient {
  protected apiClient: HyperliquidAPIClient | HyperliquidWebSocketClient;
  protected signingAdapter: SigningAdapter;
  protected isTestnet: boolean;
  protected builderConfig?: types.BuilderConfig;
  protected assetManager: AssetMetadataManager;

  constructor(config: types.InternalClientConfig) {
    // Try WebSocket first (default), fallback to REST if unavailable
    try {
      // Check if WebSocket is available in this environment
      if (typeof WebSocket !== 'undefined') {
        this.apiClient = new HyperliquidWebSocketClient({
          isTestnet: config.isTestnet || false,
        });
        debugLog('[SDK] Using WebSocket transport for lower latency');
      } else {
        throw new Error('WebSocket not available in this environment');
      }
    } catch {
      // Fallback to REST (SSR, old browsers, or WebSocket initialization failure)
      this.apiClient = new HyperliquidAPIClient({
        isTestnet: config.isTestnet || false,
      });
      debugLog('[SDK] Using REST transport (WebSocket unavailable)');
    }

    this.signingAdapter = config.signingAdapter;
    this.isTestnet = config.isTestnet || false;
    this.builderConfig = config.builder;
    this.assetManager = new AssetMetadataManager(this.isTestnet);

    // Set debug mode if specified
    if (config.debug !== undefined) {
      setDebugMode(config.debug);
      debugLog(`[SDK] Debug mode set to: ${config.debug}`);
    }
  }

  /**
   * Get builder config for current order
   * Can be overridden by subclasses to provide dynamic fees
   */
  protected getBuilderConfig(): types.BuilderConfig | undefined {
    return this.builderConfig;
  }

  /**
   * Close all connections (WebSocket cleanup)
   * Should be called when shutting down the client
   */
  protected async closeConnections(): Promise<void> {
    if ('close' in this.apiClient) {
      await this.apiClient.close();
    }
  }

  // ========== ORDER PLACEMENT METHODS ==========

  /**
   * Place an order (limit or market)
   */
  async placeOrder(params: types.PlaceOrderParams): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = this.buildOrderAction(params);
    return this.signAndSendL1Action(action, nonce);
  }

  /**
   * Place a limit order
   */
  async placeLimitOrder(params: types.LimitOrderParams): Promise<ParsedResponse> {
    return this.placeOrder({
      orders: [
        {
          ...params,
          orderType: 'limit',
          price: params.price,
          timeInForce: params.timeInForce || 'Gtc',
        },
      ],
      grouping: 'na',
    });
  }

  /**
   * Place a market order (uses IoC time in force)
   */
  async placeMarketOrder(params: types.MarketOrderParams): Promise<ParsedResponse> {
    return this.placeOrder({
      orders: [
        {
          ...params,
          orderType: 'market',
          price: params.price,
        },
      ],
      grouping: 'na',
    });
  }

  /**
   * Place a trigger order (stop loss or take profit)
   */
  async placeTriggerOrder(params: types.TriggerOrderParams): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = this.buildTriggerOrderAction(params);
    return this.signAndSendL1Action(action, nonce);
  }

  /**
   * Place both TP and SL orders for a position
   */
  async placeTpSlOrder(params: types.TpSlOrderParams): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = this.buildTpSlOrderAction(params);
    return this.signAndSendL1Action(action, nonce);
  }

  /**
   * Place multiple orders in a batch
   */
  async batchOrders(params: types.BatchOrderParams): Promise<ParsedResponse> {
    return this.placeOrder(params);
  }

  // ========== ORDER MODIFICATION METHODS ==========

  /**
   * Modify an existing order
   */
  async modifyOrder(params: types.ModifyOrderParams): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = this.buildModifyOrderAction(params);
    return this.signAndSendL1Action(action, nonce);
  }

  /**
   * Modify multiple orders in a batch
   */
  async batchModifyOrders(params: types.BatchModifyParams): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = this.buildBatchModifyAction(params);
    return this.signAndSendL1Action(action, nonce);
  }

  // ========== ORDER CANCELLATION METHODS ==========

  /**
   * Cancel orders by order ID
   */
  async cancelOrders(params: types.CancelOrderParams): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = {
      type: 'cancel',
      cancels: params.cancels.map((c) => ({ a: c.asset, o: c.orderId })),
    };
    return this.signAndSendL1Action(action, nonce);
  }

  /**
   * Cancel orders by client order ID
   */
  async cancelByClientOrderId(
    params: types.CancelByCloidParams
  ): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = {
      type: 'cancelByCloid',
      cancels: params.cancels.map((c) => ({
        asset: c.asset,
        cloid: c.clientOrderId,
      })),
    };
    return this.signAndSendL1Action(action, nonce);
  }

  /**
   * Cancel all orders for a specific asset
   */
  async cancelAllOrders(asset?: number): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action: GenericAction = {
      type: 'cancel',
    };

    if (asset !== undefined) {
      action.cancels = [{ a: asset, o: 0 }];
    } else {
      action.cancels = [];
    }

    return this.signAndSendL1Action(action, nonce);
  }

  // ========== LEVERAGE & MARGIN METHODS ==========

  /**
   * Update leverage for an asset
   */
  async updateLeverage(params: types.UpdateLeverageParams): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = {
      type: 'updateLeverage',
      asset: params.asset,
      isCross: params.isCross,
      leverage: params.leverage,
    };
    return this.signAndSendL1Action(action, nonce);
  }

  /**
   * Update isolated margin
   */
  async updateIsolatedMargin(params: types.UpdateMarginParams): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = {
      type: 'updateIsolatedMargin',
      asset: params.asset,
      isBuy: params.isBuy,
      ntli: usdToMicroUsd(params.amountUsd),
    };
    return this.signAndSendL1Action(action, nonce);
  }

  /**
   * Set margin mode (cross or isolated) and leverage
   */
  async setMarginMode(params: types.SetMarginModeParams): Promise<ParsedResponse> {
    return this.updateLeverage({
      asset: params.asset,
      isCross: params.mode === 'cross',
      leverage: params.leverage,
    });
  }

  // ========== TWAP METHODS ==========

  /**
   * Place a TWAP order
   */
  async placeTwapOrder(params: types.TwapOrderParams): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = {
      type: 'twapOrder',
      twap: {
        a: params.asset,
        b: params.isBuy,
        s: params.size,
        r: params.reduceOnly,
        m: params.durationMinutes,
        t: params.randomizeTiming,
      },
    };
    return this.signAndSendL1Action(action, nonce);
  }

  /**
   * Cancel a TWAP order
   */
  async cancelTwapOrder(params: types.CancelTwapParams): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action = {
      type: 'twapCancel',
      a: params.asset,
      t: params.twapId,
    };
    return this.signAndSendL1Action(action, nonce);
  }

  // ========== SCHEDULE CANCEL METHODS ==========

  /**
   * Schedule automatic order cancellation (dead man's switch)
   * @param timeMs Unix timestamp in milliseconds (at least 5 seconds in future)
   */
  async scheduleCancel(timeMs?: number): Promise<ParsedResponse> {
    const nonce = Date.now();
    const action: GenericAction = { type: 'scheduleCancel' };
    if (timeMs) {
      action.time = timeMs;
    }
    return this.signAndSendL1Action(action, nonce);
  }

  /**
   * Remove scheduled cancellation
   */
  async removeScheduledCancel(): Promise<ParsedResponse> {
    return this.scheduleCancel(); // No time = delete scheduled cancel
  }

  // ========== PROTECTED SIGNING METHOD ==========

  /**
   * Extract message context from action for better user-facing messages
   */
  private async extractMessageContext(
    action: GenericAction
  ): Promise<types.OrderMessageContext | types.CancelMessageContext | types.LeverageMessageContext | undefined> {
    try {
      if (action.type === 'order' && 'orders' in action && Array.isArray(action.orders) && action.orders.length > 0) {
        // Order action - extract from first order
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const order = action.orders[0] as any;
        const symbol = await this.assetManager.getSymbolByAssetId(order.a);

        // Detect if this is a TP/SL batch (normalTpsl grouping with multiple orders)
        const grouping = action.grouping as 'na' | 'normalTpsl' | 'positionTpsl' | undefined;
        const hasTPSL = grouping === 'normalTpsl' && action.orders.length > 1;

        return {
          symbol,
          side: order.b ? 'buy' : 'sell',
          orderType: order.t && 'limit' in order.t && order.t.limit.tif === 'Ioc' ? 'market' : 'limit',
          quantity: order.s,
          price: order.p,
          reduceOnly: order.r,
          isBatch: action.orders.length > 1,
          batchSize: action.orders.length,
          grouping,
          hasTPSL,
        } as types.OrderMessageContext;
      } else if (action.type === 'cancel' && 'cancels' in action && Array.isArray(action.cancels) && action.cancels.length > 0) {
        // Cancel action
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cancel = action.cancels[0] as any;
        const symbol = await this.assetManager.getSymbolByAssetId(cancel.a);

        return {
          symbol,
          orderId: cancel.oid,
          isBatch: action.cancels.length > 1,
          cancelCount: action.cancels.length,
        } as types.CancelMessageContext;
      } else if (action.type === 'updateLeverage' && 'asset' in action) {
        // Leverage update
        const symbol = await this.assetManager.getSymbolByAssetId(action.asset as number);

        return {
          symbol,
          leverage: action.leverage as number,
          isCross: action.isCross as boolean,
        } as types.LeverageMessageContext;
      }
    } catch (error) {
      // If context extraction fails, just return undefined and use fallback messages
      debugLog('[SDK] Failed to extract message context:', error);
    }

    return undefined;
  }

  protected async signAndSendL1Action(
    action: GenericAction,
    nonce: number
  ): Promise<ParsedResponse> {
    // IMPORTANT: The FULL action (including type and builder) should be hashed
    // This matches @nktkas SDK behavior - see their signL1Action implementation
    debugLog('[SDK] Action for hashing (FULL action):', JSON.stringify(action, null, 2));
    debugLog('[SDK] Nonce:', nonce);

    // 1. Create action hash using the FULL action (including type and builder)
    const actionHash = createL1ActionHash(action, nonce);
    debugLog('[SDK] Action hash:', actionHash);

    // 2. Check if adapter is ready before signing
    const adapterReady = await this.signingAdapter.isReady();
    if (!adapterReady) {
      throw new Error(
        'Signing adapter is not ready. Please ensure Privy iframe is fully initialized before calling SDK methods.'
      );
    }

    // 3. Sign using the adapter (platform-specific)
    const signature = await this.signingAdapter.signL1Action({
      action,
      nonce,
      actionHash,
      isTestnet: this.isTestnet,
    });

    // 4. Split signature
    const { r, s, v } = splitSignature(signature);

    // 5. Send to Hyperliquid API with the same action we hashed
    debugLog('[SDK] Action being sent to API:', JSON.stringify(action, null, 2));

    const hlResponse = await this.apiClient.sendRequest({
      action,
      signature: { r, s, v },
      nonce,
    });

    debugLog('[SDK] Raw Hyperliquid response:', JSON.stringify(hlResponse, null, 2));

    // 6. Extract context for better messages
    const context = await this.extractMessageContext(action);
    debugLog('[SDK] Extracted context:', JSON.stringify(context, null, 2));

    // 7. Parse response based on type
    let parsed: ParsedResponse;

    debugLog('[SDK] Checking response type...');
    debugLog('[SDK] response type:', typeof hlResponse.response === 'object' && hlResponse.response !== null ? (hlResponse.response as { type?: string }).type : 'N/A');
    debugLog('[SDK] has data:', typeof hlResponse.response === 'object' && hlResponse.response !== null && 'data' in hlResponse.response);
    debugLog('[SDK] has statuses:', typeof hlResponse.response === 'object' && hlResponse.response !== null && 'data' in hlResponse.response && typeof hlResponse.response.data === 'object' && hlResponse.response.data !== null && 'statuses' in hlResponse.response.data);

    if (typeof hlResponse.response === 'object' && hlResponse.response !== null && 'data' in hlResponse.response && typeof hlResponse.response.data === 'object' && hlResponse.response.data !== null && 'statuses' in hlResponse.response.data) {
      debugLog('[SDK] Response has statuses, checking type...');
      if (hlResponse.response.type === 'order') {
        debugLog('[SDK] Calling parseOrderResponse with context:', JSON.stringify(context, null, 2));
        parsed = parseOrderResponse(
          hlResponse as types.OrderResponse,
          context as types.OrderMessageContext
        );
        debugLog('[SDK] parseOrderResponse returned:', JSON.stringify(parsed, null, 2));
      } else if (hlResponse.response.type === 'cancel') {
        parsed = parseCancelResponse(
          hlResponse as types.CancelResponse,
          context as types.CancelMessageContext
        );
      } else {
        // Default success
        parsed = parseSuccessResponse(
          hlResponse as types.SuccessResponse,
          action.type as string,
          context as types.LeverageMessageContext
        );
      }
    } else {
      // Simple success response (leverage update, margin update, etc.)
      parsed = parseSuccessResponse(
        hlResponse as types.SuccessResponse,
        action.type as string,
        context as types.LeverageMessageContext
      );
    }

    debugLog('[SDK] Parsed response:', JSON.stringify(parsed, null, 2));

    // 7. Throw if all operations failed
    if (parsed.status === 'error') {
      throw new Error(parsed.message);
    }

    return parsed;
  }

  // ========== PRIVATE ACTION BUILDERS ==========

  private buildOrderAction(params: types.PlaceOrderParams): GenericAction {
    // IMPORTANT: Key order matters for msgpack encoding!
    // Match @nktkas SDK order: type, orders, grouping, builder
    const action: GenericAction = {
      type: 'order',
      orders: params.orders.map((order) => ({
        a: order.asset,
        b: order.isBuy,
        p: order.price,
        s: order.size,
        r: order.reduceOnly,
        t:
          order.orderType === 'market'
            ? { limit: { tif: 'Ioc' } }
            : {
                limit: {
                  tif: (order as types.LimitOrderSingle).timeInForce || 'Gtc',
                },
              },
        c: order.clientOrderId,
      })),
      grouping: params.grouping || 'na',
    };

    // Add builder config if provided
    const builderConfig = this.getBuilderConfig();
    if (builderConfig) {
      action.builder = {
        b: builderConfig.address.toLowerCase(),
        f: builderConfig.fee,
      };
    }

    return action;
  }

  private buildTriggerOrderAction(params: types.TriggerOrderParams): GenericAction {
    return {
      type: 'order',
      orders: [
        {
          a: params.asset,
          b: params.isBuy,
          p: params.limitPrice || params.triggerPrice,
          s: params.size,
          r: params.reduceOnly,
          t: {
            trigger: {
              isMarket: !params.limitPrice,
              triggerPx: params.triggerPrice,
              tpsl: params.tpsl || 'tp',
            },
          },
        },
      ],
      grouping: params.tpsl ? 'normalTpsl' : 'na',
    };
  }

  private buildTpSlOrderAction(params: types.TpSlOrderParams): GenericAction {
    const orders = [];

    if (params.takeProfit) {
      orders.push({
        a: params.asset,
        b: !params.isLong, // Opposite side to close
        p:
          params.takeProfit.limitPrice || params.takeProfit.triggerPrice,
        s: "0", // Always "0" for positionTpsl - attaches to existing position
        r: true,
        t: {
          trigger: {
            isMarket: !params.takeProfit.limitPrice,
            triggerPx: params.takeProfit.triggerPrice,
            tpsl: 'tp' as const,
          },
        },
      });
    }

    if (params.stopLoss) {
      orders.push({
        a: params.asset,
        b: !params.isLong,
        p: params.stopLoss.limitPrice || params.stopLoss.triggerPrice,
        s: "0", // Always "0" for positionTpsl - attaches to existing position
        r: true,
        t: {
          trigger: {
            isMarket: !params.stopLoss.limitPrice,
            triggerPx: params.stopLoss.triggerPrice,
            tpsl: 'sl' as const,
          },
        },
      });
    }

    return {
      type: 'order',
      orders,
      grouping: 'positionTpsl',
    };
  }

  private buildModifyOrderAction(params: types.ModifyOrderParams): GenericAction {
    return {
      type: 'modify',
      oid: params.orderId,
      order: {
        a: params.order.asset,
        b: params.order.isBuy,
        p: params.order.price,
        s: params.order.size,
        r: params.order.reduceOnly,
        t: { limit: { tif: params.order.timeInForce || 'Gtc' } },
      },
    };
  }

  private buildBatchModifyAction(params: types.BatchModifyParams): GenericAction {
    return {
      type: 'batchModify',
      modifies: params.modifies.map((m) => ({
        oid: m.orderId,
        order: {
          a: m.order.asset,
          b: m.order.isBuy,
          p: m.order.price,
          s: m.order.size,
          r: m.order.reduceOnly,
          t: { limit: { tif: m.order.timeInForce || 'Gtc' } },
        },
      })),
    };
  }
}
